import React, { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, XCircle, Download, ArrowRight, Clock, CreditCard } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import { bapi, brandLabel, downloadReceipt, fmtDate, fmtMoney } from '@/lib/billingApi';
import { trackSubscribe } from '@/lib/metaPixel';

const POLL_MS = 2000;
const SLOW_AFTER_MS = 45000;

const Detail = ({ label, value, testId }) => (
  <div className="flex justify-between gap-4 py-2 border-b border-slate-100 last:border-0">
    <span className="text-sm text-slate-500">{label}</span>
    <span className="text-sm font-semibold text-slate-900 text-right" data-testid={testId}>{value}</span>
  </div>
);

const paidMessage = (p) => (p.kind === 'switch'
  ? `You're on Pro Annual now. Your annual year starts ${fmtDate(p.period_start)}, when your paid month ends, and monthly renewals have stopped. Your receipt is on its way.`
  : 'Thank you! Your plan is active now and your receipt is on its way to your inbox.');

const Paid = ({ p }) => {
  const officials = p.product_id === 'officials_annual';
  const bundle = ['bundle_annual', 'bundle_flex'].includes(p.product_id);
  return (
    <>
      <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" strokeWidth={2.2} />
      <h1 className="text-4xl sm:text-5xl font-black uppercase text-slate-900 mt-4 leading-none" style={{ fontFamily: 'Barlow Condensed' }} data-testid="billing-result-title">Payment successful</h1>
      <p className="text-slate-600 mt-2" data-testid="billing-result-message">{paidMessage(p)}</p>
      <div className="text-left mt-6 rounded-xl border border-slate-200 bg-white px-4 py-1">
        <Detail label="Receipt" value={p.receipt_number || 'Generating…'} testId="billing-result-receipt" />
        <Detail label="Purchased" value={p.product_name} />
        <Detail label="Amount paid" value={fmtMoney(p.total, p.currency)} testId="billing-result-amount" />
        <Detail label="Payment method" value={p.card_last4 ? `${brandLabel(p.card_brand)} •••• ${p.card_last4}` : 'Credit / Debit Card'} />
        {p.kind === 'switch' && <Detail label="Annual plan starts" value={fmtDate(p.period_start)} testId="billing-result-period-start" />}
        {p.parts ? p.parts.map((x) => <Detail key={x.product_id} label={`${x.short} ${p.auto_renew ? 'renews' : 'until'}`} value={fmtDate(x.period_end)} testId={`billing-result-renews-${x.product_id}`} />)
          : p.period_end && <Detail label={p.auto_renew ? 'Next renewal' : 'Access until'} value={fmtDate(p.period_end)} testId="billing-result-period-end" />}
      </div>
      <div className="flex flex-col sm:flex-row gap-2 mt-6">
        <Link to={officials ? '/officials' : '/my-schedules'} className="flex-1"><Button className="w-full h-11 bg-[#00247D] hover:bg-[#001A5C]" data-testid="billing-result-continue-btn">{officials ? 'Open Officials Hub' : 'Start using Pro'}<ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
        {p.receipt_id && <Button variant="outline" className="h-11" onClick={() => downloadReceipt(p.receipt_id, p.receipt_number).catch((e) => toast.error(e.message))} data-testid="billing-result-download-btn"><Download className="w-4 h-4 mr-2" />Receipt</Button>}
        <Link to="/billing"><Button variant="ghost" className="h-11 w-full" data-testid="billing-result-billing-btn">Billing</Button></Link>
      </div>
      {bundle && <Link to="/officials" className="block mt-2"><Button variant="outline" className="w-full h-11" data-testid="billing-result-officials-btn">Open Officials Hub<ArrowRight className="w-4 h-4 ml-2" /></Button></Link>}
      {!officials && <BracketReturn />}
    </>
  );
};

const BRACKET_RETURN = 'sg_bracket_return';
const BracketReturn = () => {
  const [id] = useState(() => { try { return localStorage.getItem(BRACKET_RETURN); } catch { return null; } });
  if (!id) return null;
  return (
    <Link to={`/bracket-library?template=${encodeURIComponent(id)}`} className="block mt-2" onClick={() => { try { localStorage.removeItem(BRACKET_RETURN); } catch { /* ignore */ } }}>
      <Button variant="outline" className="w-full h-11 border-[#CF142B] text-[#CF142B] hover:bg-red-50" data-testid="billing-result-bracket-btn">Back to your bracket<ArrowRight className="w-4 h-4 ml-2" /></Button>
    </Link>
  );
};

const CardUpdated = ({ p }) => (
  <>
    <CreditCard className="w-16 h-16 text-emerald-500 mx-auto" strokeWidth={2} />
    <h1 className="text-4xl sm:text-5xl font-black uppercase text-slate-900 mt-4 leading-none" style={{ fontFamily: 'Barlow Condensed' }} data-testid="billing-result-title">Card updated</h1>
    <p className="text-slate-600 mt-2" data-testid="billing-result-card-updated">
      Future renewals will be charged to <b className="text-slate-800">{p.card_last4 ? `${brandLabel(p.card_brand)} •••• ${p.card_last4}` : 'your new card'}</b>. Nothing was charged today.
    </p>
    <Link to="/billing" className="block mt-6"><Button className="w-full h-11 bg-[#00247D] hover:bg-[#001A5C]" data-testid="billing-result-billing-btn">Back to billing<ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
  </>
);

const CardSwitched = ({ p }) => (
  <>
    <CreditCard className="w-16 h-16 text-emerald-500 mx-auto" strokeWidth={2} />
    <h1 className="text-4xl sm:text-5xl font-black uppercase text-slate-900 mt-4 leading-none" style={{ fontFamily: 'Barlow Condensed' }} data-testid="billing-result-title">Card billing set up</h1>
    <p className="text-slate-600 mt-2" data-testid="billing-result-card-switched">
      Your PayPal renewal is turned off. <b className="text-slate-800">{p.card_last4 ? `${brandLabel(p.card_brand)} •••• ${p.card_last4}` : 'Your card'}</b> will be charged {fmtMoney(p.renewal_amount, p.currency)} when your current year ends on <b className="text-slate-800">{fmtDate(p.period_end)}</b>. Nothing was charged today.
    </p>
    <Link to="/billing" className="block mt-6"><Button className="w-full h-11 bg-[#00247D] hover:bg-[#001A5C]" data-testid="billing-result-billing-btn">Back to billing<ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
  </>
);

const Waiting = ({ slow }) => (
  <>
    <Loader2 className="w-14 h-14 text-[#00247D] animate-spin mx-auto" />
    <h1 className="text-3xl sm:text-4xl font-black uppercase text-slate-900 mt-4" style={{ fontFamily: 'Barlow Condensed' }} data-testid="billing-result-title">Confirming your payment</h1>
    <p className="text-slate-600 mt-2">We're confirming the charge with the bank. This usually takes a few seconds. Please keep this page open.</p>
    {slow && <p className="mt-4 text-sm rounded-lg bg-sky-50 border border-sky-200 text-sky-900 px-3 py-2 flex gap-2 text-left" data-testid="billing-result-slow"><Clock className="w-4 h-4 shrink-0 mt-0.5" />Still confirming. You can safely leave: we'll activate your plan and email your receipt as soon as the bank confirms. You won't be charged twice.</p>}
  </>
);

const TestPaid = ({ p }) => (
  <>
    <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto" strokeWidth={2.2} />
    <h1 className="text-4xl sm:text-5xl font-black uppercase text-slate-900 mt-4 leading-none" style={{ fontFamily: 'Barlow Condensed' }} data-testid="billing-result-title">Test charge successful</h1>
    <p className="text-slate-600 mt-2" data-testid="billing-result-test-note">
      The gateway charged <b className="text-slate-800">{fmtMoney(p.total, p.currency)}</b>. Check that the Plug'n Pay portal and your card show USD, then refund it from Admin → Sales → Gateway &amp; settings.
    </p>
    <div className="text-left mt-6 rounded-xl border border-slate-200 bg-white px-4 py-1">
      <Detail label="Order ID" value={p.order_id} testId="billing-result-order-id" />
      <Detail label="Payment method" value={p.card_last4 ? `${brandLabel(p.card_brand)} •••• ${p.card_last4}` : 'Credit / Debit Card'} />
    </div>
    <Link to="/admin?tab=sales&sub=settings" className="block mt-6"><Button className="w-full h-11 bg-[#00247D] hover:bg-[#001A5C]" data-testid="billing-result-admin-btn">Back to Admin<ArrowRight className="w-4 h-4 ml-2" /></Button></Link>
  </>
);

const PAID_VIEW = { card_update: CardUpdated, card_switch: CardSwitched, test: TestPaid };

const retryLink = (p) => (p?.kind === 'test' ? '/admin' : ['switch', 'card_switch'].includes(p?.kind) ? '/billing'
  : p?.product_id === 'officials_annual' ? '/pricing#officials-addon' : ['bundle_annual', 'bundle_flex'].includes(p?.product_id) ? '/pricing#bundle' : '/pricing');

const Failed = ({ p, cancelled }) => (
  <>
    <XCircle className="w-16 h-16 text-red-500 mx-auto" strokeWidth={2.2} />
    <h1 className="text-3xl sm:text-4xl font-black uppercase text-slate-900 mt-4" style={{ fontFamily: 'Barlow Condensed' }} data-testid="billing-result-title">{cancelled ? 'Checkout cancelled' : "Payment didn't go through"}</h1>
    <p className="text-slate-700 mt-2 font-medium" data-testid="billing-result-reason">{cancelled ? 'No payment was taken.' : (p?.failure_reason || 'Your card was not charged.')}</p>
    <p className="text-slate-500 mt-1 text-sm">You can try again or pay with PayPal.</p>
    <div className="flex flex-col sm:flex-row gap-2 mt-6 justify-center">
      <Link to={retryLink(p)}><Button className="h-11 bg-[#00247D] hover:bg-[#001A5C] w-full" data-testid="billing-result-retry-btn">Try again</Button></Link>
      <Link to="/billing"><Button variant="outline" className="h-11 w-full">Billing</Button></Link>
    </div>
  </>
);

export default function BillingResult() {
  const [params] = useSearchParams();
  const order = params.get('order') || sessionStorage.getItem('sg-last-order');
  const result = params.get('result') || '';
  const { checkAuth, isAuthenticated, isLoading } = useAuth();
  const [p, setP] = useState(null);
  const [slow, setSlow] = useState(false);
  const started = useRef(Date.now());
  const tracked = useRef(false);

  useEffect(() => {
    if (!order || isLoading || !isAuthenticated) return undefined;
    let stop = false;
    let timer;
    const tick = async () => {
      try {
        const s = await bapi(`/billing/payments/${order}/status`, { query: { result } });
        if (stop) return;
        setP(s);
        if (s.status === 'paid') {
          checkAuth();
          if (!tracked.current && !['test', 'card_switch'].includes(s.kind)) { tracked.current = true; trackSubscribe({ value: s.total, currency: s.currency, planId: s.product_id }); }
          return;
        }
        if (['failed', 'cancelled', 'voided', 'refunded'].includes(s.status)) return;
      } catch { /* keep polling */ }
      setSlow(Date.now() - started.current > SLOW_AFTER_MS);
      timer = setTimeout(tick, POLL_MS);
    };
    tick();
    return () => { stop = true; clearTimeout(timer); };
  }, [order, result, isAuthenticated, isLoading, checkAuth]);

  const status = p?.status;
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md text-center bg-white rounded-3xl shadow-xl border border-slate-100 p-6 sm:p-10 animate-in fade-in slide-in-from-bottom-2 duration-500" data-testid="billing-result">
        {!order ? <Failed cancelled /> : !isLoading && !isAuthenticated ? (
          <p className="text-slate-600">Please <Link to="/login" className="text-[#00247D] font-semibold underline">log in</Link> to see your payment.</p>
        ) : status === 'paid' ? React.createElement(PAID_VIEW[p.kind] || Paid, { p })
          : ['failed', 'voided'].includes(status) ? <Failed p={p} />
            : status === 'cancelled' ? <Failed p={p} cancelled />
              : <Waiting slow={slow} />}
      </div>
    </div>
  );
}
